import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { styled } from "styled-components/native";
import { formatCardDueLine } from "#format-card-due-line";
import { DUE_LINE_PADDING_TOP_PX, getDueLineFontSize } from "#sizing";

// We pass `size` into the font-size declaration instead of
// `clamp(11px, 5cqi, 18px)` because that CSS declaration drops on native and
// the label collapses to zero height — verified via inspect-element on
// iPhone 16e.
//
// `line-height` intentionally pegged to the font size (not the unitless `1`
// the web build uses) because React Native interprets unitless line-height
// as literal pixels rather than the CSS multiplier — `line-height: 1` would
// crush the box to 1px tall and hide the text entirely on iOS/Android.
const DueLine = styled.Text<{ $color: string; $fontSize: number }>`
  flex-shrink: 0;
  padding-top: ${DUE_LINE_PADDING_TOP_PX}px;
  font-size: ${(p) => p.$fontSize}px;
  font-weight: 500;
  line-height: ${(p) => p.$fontSize}px;
  text-align: center;
  color: ${(p) => p.$color};
`;

// Mirrored from apps/haniverse-expo/global.css. See pinyin-keyboard-key.tsx for why
// this is hardcoded instead of read from the uniwind theme.
const DUE_LINE_COLOR_BY_THEME = {
  dark: "hsl(0, 0%, 63.9%)",
  light: "hsl(0, 0%, 45.1%)",
} as const;

export function CardDetailsTileDueLine({
  dueLabel,
  isMastered = false,
  size,
}: {
  /**
   * Compact due-time token from {@link formatCardDueLabel}: `"5d"` / `"3h"` /
   * `"5m"` for a scheduled card, `"Due"` once it is due, and `undefined` when the
   * card has no schedule yet. Expanded into the due line ("Due in 5d" / "Due now"
   * / "No due date").
   */
  dueLabel?: string;
  isMastered?: boolean;
  /** Tile size in px; the due-line font size is a fraction of this. */
  size: number;
}) {
  const scheme = useTheme();
  const color = DUE_LINE_COLOR_BY_THEME[scheme === "dark" ? "dark" : "light"];
  const fontSize = getDueLineFontSize(size);

  return (
    <DueLine $color={color} $fontSize={fontSize} numberOfLines={1}>
      {formatCardDueLine(dueLabel, { isMastered })}
    </DueLine>
  );
}
